import moment from "moment";
import { ShiftGroupDto } from "@/types/shift.type";
import { RatingDto } from "@/types/rating.type";
import { ShiftGroupResponseDto } from "@/types/shift-response.type";
import { ShiftStatus } from "@/common/constants";

export class Shift {
  constructor(shift: ShiftGroupDto, shiftStatus: ShiftStatus) {
    const start = moment(shift.startDateTime).format("h:mm A");
    const end = moment(shift.endDateTime).format("h:mm A");
    const date = moment(shift.startDateTime).format("dddd, MM/DD/YYYY");

    this.id = shift.id;
    this.shiftId = shift.shiftId;
    this.postedByUserId = shift.postedByUserId;
    this.location = shift.name;
    this.officeId = shift.officeId;
    this.date = new Date(date);
    this.time = `${start} - ${end}`;
    this.position = shift.positionName;
    this.rate = `$${shift.payRate.toFixed(2)}/hr`;
    this.rawRate = shift.payRate;
    this.speakSpanish = shift.speakSpanish;
    this.doubleChannel = shift.doubleChannel;
    this.positionAbbreviation = shift.positionAbbreviation;
    this.posterFirstName = shift.posterFirstName;
    this.posterLastName = shift.posterLastName;
    this.postDate = new Date(shift.postDate);
    this.shiftStatus = shiftStatus.value;
    this.shiftRating = null;

    if (shift.rating) {
      this.shiftRating = shift.rating;
    }
  }

  id: number;
  shiftId: number;
  postedByUserId: number;
  posterFirstName: string;
  posterLastName: string;
  postDate: Date;
  location: string;
  officeId: number;
  date: Date;
  time: string;
  position: string;
  positionAbbreviation: string | null;
  rate: string;
  rawRate: number;
  speakSpanish: boolean;
  doubleChannel: boolean;
  shiftStatus: string;
  shiftRating: RatingDto | null;
}

export class AcceptedShift extends Shift {
  constructor(shift: ShiftGroupDto, statuses: number[], shiftStatus: ShiftStatus) {
    super(shift, shiftStatus);

    const response = shift.responses
      .find((response: ShiftGroupResponseDto) =>
        statuses.some(status => response.status === status)
      ) as ShiftGroupResponseDto;

    const name = `${response.firstName} ${response.lastName}`;

    this.userId = response.userId;
    this.name = name;
    this.rating = response.averageRatingOfDt ?? 0;
    this.email = response.email;
    this.phone = response.phoneNumber;
    this.isFavorite = response.isFavorite;
    this.acceptedCounterOffer = response.counterOffer ? `$${response.counterOffer.toFixed(2)}/hr` : null;
  }

  userId: number;
  name: string;
  rating: number;
  email: string | null;
  phone: string | null;
  isFavorite: boolean;
  acceptedCounterOffer: string | null;
}

export interface OpenShift extends Shift {
  favoriteOnly: boolean;
}

export interface RequestedShift extends Shift {
  requestedWorkers: RequestedWorker[];
}

export class RequestedWorker {
  constructor(response: ShiftGroupResponseDto) {
    const name = `${response.firstName} ${response.lastName}`;

    this.name = name;
    this.email = response.email;
    this.phone = response.phoneNumber;
    this.rating = response.averageRatingOfDt ?? 0;
    this.userId = response.userId;
    this.shiftResponseId = response.id;
    this.isFavorite = response.isFavorite;
    this.counterOffer = response.counterOffer;
  }

  name: string;
  rating: number;
  email: string | null;
  phone: string | null;
  userId: number;
  shiftResponseId: number;
  isFavorite: boolean;
  counterOffer: number | null;
}

export interface EmployeeInfo {
  id: number;
  name: string;
  timeIn: string;
  timeOut: string;
  position: string;
  rate: string;
  initials: string;
  officeId: number;
  email: string  | null;
  phone: string | null;
  rating: number;
  date: Date;
  isFavorite: boolean;
}